/**
 * RS-8 — the vendor's answer page: /q/{code}.
 *
 * The same pattern as /p/{code} — no login, no shell. One question, whatever
 * it is about, and the one thing this page is for: the answer. When the firm
 * has offered choices, picking one is the whole job.
 */
import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import { cacheBranding } from '@/utils/skin';
import '@/styles/question-answer.css';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const bare = axios.create();

const day = (value) => {
  if (!value) return '';
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? String(value).slice(0, 10) : d.toLocaleDateString();
};

export default function QuestionAnswerPage() {
  const { code } = useParams();
  const [q, setQ] = useState(null);
  const [missing, setMissing] = useState(false);
  const [choice, setChoice] = useState('');
  const [text, setText] = useState('');
  const [answers, setAnswers] = useState([]);
  const [thanked, setThanked] = useState(false);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    bare.get(`${API}/q/${code}`)
      .then((r) => {
        setQ(r.data);
        setAnswers(r.data?.your_answers || []);
        setThanked(Boolean(r.data?.answered));
        if (r.data?.branding) cacheBranding(r.data.branding);
      })
      .catch(() => setMissing(true));
  }, [code]);

  const send = async () => {
    if (!choice && !text.trim()) { setError('Pick one or write an answer'); return; }
    setSending(true);
    setError('');
    try {
      const r = await bare.post(`${API}/q/${code}/answer`,
        { choice, text: text.trim() });
      setAnswers([...answers, r.data?.answer || { choice, text: text.trim() }]);
      setChoice('');
      setText('');
      setThanked(true);
    } catch (e) {
      setError(e?.response?.data?.detail || 'That did not send. Try again.');
    }
    setSending(false);
  };

  if (missing) {
    return (
      <div className="kqa" data-testid="question-answer">
        <div className="kqa-empty" data-testid="question-answer-missing">
          <b>No question at this address.</b>
        </div>
      </div>
    );
  }
  if (!q) return <div className="kqa" data-testid="question-answer-loading" />;

  const refs = [q.drawing_reference ? `Drawing: ${q.drawing_reference}` : '',
    q.spec_reference ? `Spec: ${q.spec_reference}` : ''].filter(Boolean);

  return (
    <div className="kqa" data-testid="question-answer">
      <div className="kqa-head">
        <span className="kqa-eyebrow" data-testid="question-firm">
          {[q.firm_name, q.project_name].filter(Boolean).join(' · ')}
        </span>
        <h1 className="kqa-title" data-testid="question-title">
          {`${q.rfi_number} · ${q.subject}`}
        </h1>
      </div>

      <div className="kqa-card">
        <p className="kqa-lab">The question</p>
        <p className="kqa-read" data-testid="question-body">{q.question}</p>
        {q.background ? (
          <>
            <p className="kqa-lab">Background</p>
            <p className="kqa-read" data-testid="question-background">{q.background}</p>
          </>
        ) : null}
        {refs.length ? (
          <div className="kqa-refs" data-testid="question-refs">
            {refs.map((line) => (
              <p className="kqa-small" key={line}>{line}</p>
            ))}
          </div>
        ) : null}
        {(q.attachments || []).length ? (
          <div className="kqa-atts" data-testid="question-attachments">
            {q.attachments.map((a, i) => (
              <div className="kqa-att" key={`${a.label}-${i}`}
                data-testid={`question-attachment-${i}`}>
                {a.preview_url ? (
                  <img src={a.preview_url} alt={a.label} />
                ) : null}
                <p className="kqa-small"><b>{a.label}</b></p>
                {a.share_url ? (
                  <a className="kqa-link" href={a.share_url} target="_blank" rel="noreferrer"
                    data-testid={`question-attachment-link-${i}`}>Open it (no login)</a>
                ) : null}
              </div>
            ))}
          </div>
        ) : null}
        {q.due_date ? (
          <p className="kqa-small kqa-late" data-testid="question-due">
            {`Answer by ${day(q.due_date)}`}
          </p>
        ) : null}
      </div>

      {thanked ? (
        <div className="kqa-card" data-testid="question-thanks">
          <p className="kqa-read" data-testid="question-thanks-line">
            {`Thank you — ${q.firm_name} has your answer.`}
          </p>
          {answers.map((a, i) => (
            <div className="kqa-said" key={`said-${i}`} data-testid={`question-said-${i}`}>
              {a.choice ? <p className="kqa-small"><b>{a.choice}</b></p> : null}
              {a.text ? <p className="kqa-read">{a.text}</p> : null}
              {a.created_at ? <p className="kqa-small">{day(a.created_at)}</p> : null}
            </div>
          ))}
          <button type="button" className="kqa-out" onClick={() => setThanked(false)}
            data-testid="question-add-more">Add to your answer</button>
        </div>
      ) : (
        <div className="kqa-card" data-testid="question-form">
          <p className="kqa-lab">Your answer</p>
          {(q.options || []).length ? (
            <>
              <p className="kqa-small" data-testid="question-pick-one">Pick one</p>
              {q.options.map((option, i) => (
                <button type="button" key={`${option}-${i}`}
                  className="kqa-pick"
                  aria-pressed={choice === option}
                  onClick={() => setChoice(choice === option ? '' : option)}
                  data-testid={`question-option-${i}`}>
                  <span className="kqa-dot" />
                  <span>{option}</span>
                </button>
              ))}
            </>
          ) : null}
          <textarea className="kqa-field" rows={4} value={text}
            placeholder={(q.options || []).length
              ? 'Or say it another way' : 'Your answer'}
            onChange={(e) => setText(e.target.value)}
            data-testid="question-text" />
          {error ? (
            <p className="kqa-small kqa-late" data-testid="question-error">{error}</p>
          ) : null}
          <button type="button" className="kqa-go" disabled={sending} onClick={send}
            data-testid="question-send">
            {sending ? 'Sending…' : 'Send your answer'}
          </button>
        </div>
      )}
    </div>
  );
}
